import { useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import PinballGame from "./components/PinballGame";
import ResultDisplay from "./components/ResultDisplay";
import ControlPanel from "./components/ControlPanel";
import CelebrationOverlay from "./components/CelebrationOverlay";
import SharePanel from "./components/SharePanel";
import IntroScreen from "./components/IntroScreen";
import { initAudio } from "./utils/sounds";

const MESSAGES = {
  ready: "게임 시작 버튼을 눌러봐!",
  pull: "플런저를 아래로 당겨서 발사!",
  flying: "공이 튕기는 중… 어디로 갈까!",
  settled: "착지! 다음 공 준비~",
  duplicate: "중복! 공이 다시 튕겨나간다 ㅋㅋ",
  complete: "오늘은 이거다. 안되면 내 탓 아님",
  auto: "자동으로 뽑았다! 이게 운명이야",
  copied: "번호 복사 완료! 행운을 빌어요 🍀",
};

const BALL_COLORS_CSS = [
  "from-amber-400 to-amber-600",
  "from-rose-400 to-rose-600",
  "from-emerald-400 to-emerald-600",
  "from-sky-400 to-sky-600",
  "from-violet-400 to-violet-600",
  "from-orange-400 to-orange-600",
];

export default function Home() {
  const [selectedNumbers, setSelectedNumbers] = useState<number[]>([]);
  const [message, setMessage] = useState(MESSAGES.ready);
  const [isComplete, setIsComplete] = useState(false);
  const [isStarted, setIsStarted] = useState(false);
  const [isBallFlying, setIsBallFlying] = useState(false);
  const [currentBallIndex, setCurrentBallIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [showCelebration, setShowCelebration] = useState(false);
  const [copiedFlash, setCopiedFlash] = useState(false);
  const [hasPlayedOnce, setHasPlayedOnce] = useState(false);
  const [showIntro, setShowIntro] = useState(true);
  const [showInstallGuide, setShowInstallGuide] = useState(false);
  const gameRef = useRef<any>(null);

  const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
  const isAndroid = /android/i.test(navigator.userAgent);
  const isMobile = isIOS || isAndroid;

  const handleBallSettled = useCallback((ballIndex: number, number: number) => {
    setSelectedNumbers(prev => {
      const next = [...prev];
      next[ballIndex] = number;
      return next;
    });
    setScore(prev => prev + number * 10);
    setIsBallFlying(false);

    const nextIndex = ballIndex + 1;
    if (nextIndex < 6) {
      setCurrentBallIndex(nextIndex);
      setMessage(MESSAGES.pull);
      setTimeout(() => {
        if (gameRef.current?.prepareBall) {
          gameRef.current.prepareBall();
        }
      }, 100);
    }
  }, []);

  const handleAllNumbersSelected = useCallback((numbers: number[]) => {
    setSelectedNumbers(numbers);
    setScore(numbers.reduce((a, b) => a + b * 10, 0));
    setMessage(MESSAGES.complete);
    setIsComplete(true);
    setIsBallFlying(false);
    setShowCelebration(true);
  }, []);

  const handleStart = useCallback(() => {
    try {
      initAudio();
    } catch (e) {
      console.warn("Audio initialization failed:", e);
    }

    setSelectedNumbers([]);
    setIsComplete(false);
    setScore(0);
    setCurrentBallIndex(0);
    setIsStarted(true);
    setIsBallFlying(false);
    setMessage(MESSAGES.pull);
    setShowCelebration(false);
    setCopiedFlash(false);
    setHasPlayedOnce(true);

    if (gameRef.current?.reset) {
      gameRef.current.reset();
    }
    setTimeout(() => {
      if (gameRef.current?.initWorld) {
        gameRef.current.initWorld();
      }
      setTimeout(() => {
        if (gameRef.current?.prepareBall) {
          gameRef.current.prepareBall();
        }
      }, 100);
    }, 50);
  }, []);

  const handleLaunchEnd = useCallback(() => {
    setIsBallFlying(false);
  }, []);

  const handleReset = useCallback(() => {
    setSelectedNumbers([]);
    setIsComplete(false);
    setIsBallFlying(false);
    setCurrentBallIndex(0);
    setScore(0);
    setShowCelebration(false);
    setCopiedFlash(false);
    setMessage(MESSAGES.ready);
    setIsStarted(false);
    if (gameRef.current?.reset) {
      gameRef.current.reset();
    }
  }, []);

  const handleAutoGenerate = useCallback(() => {
    const numbers: number[] = [];
    while (numbers.length < 6) {
      const num = Math.floor(Math.random() * 45) + 1;
      if (!numbers.includes(num)) numbers.push(num);
    }
    setSelectedNumbers(numbers);
    setScore(numbers.reduce((a, b) => a + b * 10, 0));
    setMessage(MESSAGES.auto);
    setIsComplete(true);
    setShowCelebration(true);
  }, []);

  const handleCopy = useCallback(async () => {
    try {
      const text = [...selectedNumbers].sort((a, b) => a - b).join(", ");
      await navigator.clipboard.writeText(text);
      setMessage(MESSAGES.copied);
      setCopiedFlash(true);
      setTimeout(() => {
        setCopiedFlash(false);
        setMessage(MESSAGES.complete);
      }, 2000);
    } catch {
      setMessage("복사에 실패했습니다. 수동으로 복사해주세요.");
    }
  }, [selectedNumbers]);

  const handleCloseCelebration = useCallback(() => {
    setShowCelebration(false);
  }, []);

  const progressCount = selectedNumbers.filter(n => n !== undefined).length;

  return (
    <div className="min-h-screen bg-[#1a1a2e] relative overflow-hidden flex flex-col">
      {showIntro && <IntroScreen onFinish={() => setShowIntro(false)} />}

      {showCelebration && (
        <CelebrationOverlay
          numbers={selectedNumbers}
          onClose={handleCloseCelebration}
          onCopy={handleCopy}
        />
      )}

      {/* Install Guide Modal */}
      {showInstallGuide && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={() => setShowInstallGuide(false)}
        >
          <div
            className="bg-gray-900 border border-amber-600/40 rounded-t-3xl sm:rounded-2xl p-6 w-full max-w-sm mx-auto shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 flex items-center justify-center bg-amber-500/20 rounded-lg">
                  <i className="ri-smartphone-line text-amber-400 text-lg" />
                </div>
                <h3 className="text-white font-bold text-base" style={{ fontFamily: "Orbitron, sans-serif" }}>
                  홈 화면에 추가
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setShowInstallGuide(false)}
                className="w-7 h-7 flex items-center justify-center text-gray-500 hover:text-white cursor-pointer"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>

            {isIOS && (
              <div className="space-y-3">
                <p className="text-gray-400 text-sm mb-3">Safari에서 아래 순서로 추가하세요</p>
                {[
                  { icon: "ri-safari-line", text: "Safari 브라우저로 이 페이지 열기" },
                  { icon: "ri-share-box-line", text: "하단 공유 버튼(□↑) 탭" },
                  { icon: "ri-add-box-line", text: '"홈 화면에 추가" 선택 후 추가' },
                ].map((step, i) => (
                  <div key={i} className="flex items-center gap-3 bg-gray-800/60 rounded-xl px-4 py-3">
                    <span className="w-6 h-6 rounded-full bg-amber-500/20 text-amber-400 text-xs font-bold flex items-center justify-center flex-shrink-0">
                      {i + 1}
                    </span>
                    <i className={`${step.icon} text-amber-400 text-base flex-shrink-0`} />
                    <span className="text-gray-300 text-sm">{step.text}</span>
                  </div>
                ))}
              </div>
            )}

            {isAndroid && (
              <div className="space-y-3">
                <p className="text-gray-400 text-sm mb-3">Chrome에서 아래 순서로 추가하세요</p>
                {[
                  { icon: "ri-chrome-line", text: "Chrome 브라우저로 이 페이지 열기" },
                  { icon: "ri-more-2-fill", text: "우측 상단 ⋮ 메뉴 탭" },
                  { icon: "ri-add-box-line", text: '"홈 화면에 추가" 선택 후 추가' },
                ].map((step, i) => (
                  <div key={i} className="flex items-center gap-3 bg-gray-800/60 rounded-xl px-4 py-3">
                    <span className="w-6 h-6 rounded-full bg-amber-500/20 text-amber-400 text-xs font-bold flex items-center justify-center flex-shrink-0">
                      {i + 1}
                    </span>
                    <i className={`${step.icon} text-amber-400 text-base flex-shrink-0`} />
                    <span className="text-gray-300 text-sm">{step.text}</span>
                  </div>
                ))}
              </div>
            )}

            {!isMobile && (
              <div className="space-y-3">
                <p className="text-gray-400 text-sm mb-3">모바일 기기에서 접속 후 추가하세요</p>
                <div className="flex gap-4 justify-center">
                  <div className="flex flex-col items-center gap-2 bg-gray-800/60 rounded-xl px-5 py-4">
                    <i className="ri-apple-line text-gray-300 text-2xl" />
                    <span className="text-gray-400 text-xs">iPhone</span>
                    <span className="text-gray-500 text-[10px] text-center">Safari → 공유 → 홈 화면에 추가</span>
                  </div>
                  <div className="flex flex-col items-center gap-2 bg-gray-800/60 rounded-xl px-5 py-4">
                    <i className="ri-android-line text-gray-300 text-2xl" />
                    <span className="text-gray-400 text-xs">Android</span>
                    <span className="text-gray-500 text-[10px] text-center">Chrome → ⋮ → 홈 화면에 추가</span>
                  </div>
                </div>
              </div>
            )}

            <button
              type="button"
              onClick={() => setShowInstallGuide(false)}
              className="mt-5 w-full bg-amber-500 hover:bg-amber-400 text-gray-900 font-bold py-3 rounded-xl text-sm cursor-pointer transition-all whitespace-nowrap"
              style={{ fontFamily: "Orbitron, sans-serif" }}
            >
              확인
            </button>
          </div>
        </div>
      )}

      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-orange-500/5 rounded-full blur-3xl" />
      </div>

      {/* ===== MOBILE TOP BAR ===== */}
      <header className="relative z-10 border-b border-amber-900/30 flex-shrink-0">
        <div className="px-3 sm:px-6 lg:px-8 py-3 lg:py-5 max-w-7xl mx-auto w-full">
          {/* Row 1: Logo + Buttons */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              {/* 로고: 공 + 숫자 6 */}
              <div className="w-9 h-9 lg:w-11 lg:h-11 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/30 flex-shrink-0 border-2 border-amber-300/40 relative">
                <span className="text-white font-black text-lg lg:text-xl leading-none select-none" style={{ fontFamily: "Orbitron, sans-serif", textShadow: "0 1px 4px rgba(0,0,0,0.4)" }}>6</span>
                {/* 공 하이라이트 */}
                <div className="absolute top-1 left-1.5 w-2.5 h-1.5 bg-white/40 rounded-full blur-[1px]" />
              </div>
              <div className="min-w-0">
                <h1 className="font-bold text-white leading-tight flex flex-col" style={{ fontFamily: "Orbitron, sans-serif" }}>
                  <span className="text-white drop-shadow-[0_0_8px_rgba(255,255,255,0.4)] text-[15px] lg:text-[18px] tracking-wider">PINBALL</span>
                  <span className="flex items-baseline gap-1">
                    <span className="text-transparent bg-clip-text bg-gradient-to-b from-amber-300 via-orange-400 to-orange-600 font-black text-[15px] lg:text-[18px] leading-none drop-shadow-[0_2px_4px_rgba(251,146,60,0.6)]" style={{ fontFamily: "Orbitron, sans-serif", letterSpacing: "0.35em" }}>SIX</span>
                  </span>
                </h1>
                <p className="text-gray-500 text-[10px] lg:text-xs hidden sm:block">핀볼로 뽑는 오늘의 행운 번호</p>
              </div>
            </div>

            {/* Action Buttons - always visible */}
            <div className="flex items-center gap-2 flex-shrink-0">
              {!isStarted ? (
                <>
                  <button
                    type="button"
                    onClick={handleStart}
                    className="relative overflow-hidden bg-gradient-to-r from-violet-500 to-purple-500 hover:from-violet-400 hover:to-purple-400 text-white font-bold py-1.5 px-3 lg:py-3 lg:px-6 rounded-full transition-all duration-200 flex items-center gap-2 whitespace-nowrap cursor-pointer hover:scale-[1.05] active:scale-95 text-sm lg:text-lg tracking-wide animate-glow-breathe-violet animate-shimmer-sweep"
                    style={{ fontFamily: "Orbitron, sans-serif" }}
                  >
                    <span className="absolute inset-0 rounded-full bg-violet-400/30 animate-ping-slow pointer-events-none" />
                    <span className="absolute inset-0 rounded-full border-2 border-violet-300/50 animate-ping-slow pointer-events-none" style={{ animationDelay: '0.5s' }} />
                    <i className="ri-notification-3-fill text-sm lg:text-xl relative z-10 drop-shadow-[0_0_6px_rgba(255,255,255,0.8)]" />
                    <span className="relative z-10 drop-shadow-[0_0_6px_rgba(255,255,255,0.6)]">게임 시작</span>
                  </button>
                  <button
                    type="button"
                    onClick={handleAutoGenerate}
                    className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-gray-900 font-bold py-1.5 px-3 lg:py-3 lg:px-5 rounded-full transition-all duration-200 flex items-center gap-1.5 whitespace-nowrap cursor-pointer shadow-lg shadow-amber-500/20 hover:scale-[1.05] active:scale-95 text-sm lg:text-lg tracking-wide"
                    style={{ fontFamily: "Orbitron, sans-serif" }}
                  >
                    <i className="ri-magic-fill text-sm lg:text-lg" />
                    자동
                  </button>
                </>
              ) : (
                <>
                  {/* Score badge - desktop only */}
                  <div className="hidden lg:flex bg-gray-800/80 rounded-xl px-4 py-2 border border-amber-600/20 items-center gap-2">
                    <span className="text-gray-500 text-xs" style={{ fontFamily: "Orbitron, sans-serif" }}>SCORE</span>
                    <span className="text-amber-400 font-bold text-lg tracking-wider" style={{ fontFamily: "Orbitron, sans-serif" }}>
                      {String(score).padStart(6, "0")}
                    </span>
                  </div>
                  {/* 광고 배너 - 모바일/데스크탑 모두 표시 */}
                  <a
                    href="https://bookk.co.kr/bookStore/68d3f712ac751d87d0a8489c"
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="flex-shrink-0 overflow-hidden rounded-lg border border-amber-600/30 hover:border-amber-400/60 transition-all duration-200 active:scale-95"
                    style={{ height: "34px" }}
                  >
                    <img
                      src="https://storage.readdy-site.link/project_files/7a66d76a-ad39-4bcf-8439-c5dc65375d70/e1ac0771-cdae-42bd-ae8a-31dbc6c32ab1_Untitled-1.jpg?v=667f8f7ac8e9a6eb98144c1d58b57c25"
                      alt="광고"
                      className="h-full w-auto object-contain"
                    />
                  </a>
                  <button
                    type="button"
                    onClick={handleReset}
                    className="bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-500 hover:to-gray-600 text-white font-bold py-2 px-4 lg:py-3 lg:px-5 rounded-full transition-all duration-200 flex items-center gap-1.5 whitespace-nowrap cursor-pointer shadow-lg hover:scale-[1.05] active:scale-95 text-xs lg:text-sm tracking-wide"
                    style={{ fontFamily: "Orbitron, sans-serif" }}
                  >
                    <i className="ri-restart-fill text-sm lg:text-base" />
                    리셋
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Row 2: Progress bar (visible when game started on mobile) */}
          {isStarted && (
            <div className="mt-2.5 lg:hidden">
              <div className="flex items-center gap-2.5">
                {/* Ball dots */}
                <div className="flex items-center gap-1.5">
                  {BALL_COLORS_CSS.map((col, i) => (
                    <div
                      key={i}
                      className={`w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-bold transition-all duration-300 ${
                        i < progressCount
                          ? `bg-gradient-to-br ${col} text-white shadow-md`
                          : i === currentBallIndex && !isComplete
                          ? `bg-gradient-to-br ${col} text-white shadow-lg ring-2 ring-white/30 animate-pulse`
                          : "bg-gray-700/50 text-gray-600 border border-dashed border-gray-600"
                      }`}
                      style={{ fontFamily: "Orbitron, sans-serif" }}
                    >
                      {i < progressCount ? "\u2713" : i + 1}
                    </div>
                  ))}
                </div>
                {/* Progress bar */}
                <div className="flex-1 bg-gray-700/50 rounded-full h-2 overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-amber-400 to-orange-500 transition-all duration-700 rounded-full relative"
                    style={{ width: `${(progressCount / 6) * 100}%` }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-pulse" />
                  </div>
                </div>
                <span className="text-amber-400 font-bold text-xs flex-shrink-0" style={{ fontFamily: "Orbitron, sans-serif" }}>
                  {progressCount}/6
                </span>
              </div>
              {/* Status message */}
              <div className="mt-1.5 flex items-center gap-1.5">
                <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${isBallFlying ? "bg-red-400 animate-ping" : isStarted && !isComplete ? "bg-amber-400 animate-pulse" : "bg-amber-400"}`} />
                <span className="text-amber-300/80 text-[11px] truncate">{message}</span>
              </div>
            </div>
          )}

          {/* Complete bar with numbers */}
          {isComplete && selectedNumbers.filter(n => n !== undefined).length === 6 && (
            <div className={`mt-2.5 flex items-center justify-center gap-2 bg-gray-900/90 border-2 rounded-full px-3 py-1.5 lg:px-4 lg:py-2 shadow-xl transition-all duration-300 ${copiedFlash ? "border-emerald-400 shadow-emerald-500/30" : "border-amber-500/50 shadow-amber-500/20"}`}>
              <i className="ri-star-fill text-amber-400 text-xs lg:text-sm" />
              <div className="flex items-center gap-1">
                {[...selectedNumbers].sort((a, b) => a - b).map((num, i) => (
                  <span
                    key={i}
                    className={`w-7 h-7 lg:w-9 lg:h-9 rounded-full bg-gradient-to-br ${BALL_COLORS_CSS[i]} text-white text-[10px] lg:text-xs font-bold flex items-center justify-center shadow-md`}
                    style={{ fontFamily: "Orbitron, sans-serif" }}
                  >
                    {num}
                  </span>
                ))}
              </div>
              <button
                type="button"
                onClick={handleCopy}
                className={`ml-1 px-2.5 py-1 lg:px-3 lg:py-1.5 rounded-full text-[10px] lg:text-xs font-bold flex items-center gap-1 whitespace-nowrap cursor-pointer transition-all duration-200 ${copiedFlash ? "bg-emerald-500 text-white" : "bg-amber-500 hover:bg-amber-400 text-gray-900"}`}
                style={{ fontFamily: "Orbitron, sans-serif" }}
              >
                <i className={`${copiedFlash ? "ri-check-line" : "ri-file-copy-line"} text-xs`} />
                {copiedFlash ? "복사됨!" : "복사"}
              </button>
            </div>
          )}
        </div>
      </header>

      {/* ===== MAIN CONTENT ===== */}
      <main className="relative z-10 flex-1 flex flex-col lg:block">
        <div className="max-w-7xl mx-auto w-full lg:px-6 lg:py-6 flex flex-col lg:block flex-1">

          {/* Desktop: status message */}
          {isStarted && !isComplete && (
            <div className="hidden lg:flex justify-center mb-5">
              <div className="bg-gray-900/80 border border-amber-600/30 rounded-full px-6 py-2.5 inline-flex items-center gap-2 shadow-lg">
                <div className={`w-2 h-2 rounded-full ${isBallFlying ? "bg-red-400 animate-ping" : "bg-amber-400 animate-pulse"}`} />
                <span className="text-amber-300 font-bold text-sm">{message}</span>
              </div>
            </div>
          )}

          <div className="flex flex-col lg:grid lg:grid-cols-[1fr_320px] lg:gap-6 flex-1">
            {/* Game Canvas - full width on mobile */}
            <div className="flex justify-center flex-1 lg:flex-none">
              <div className="w-full lg:max-w-[620px]">
                <PinballGame
                  ref={gameRef}
                  onBallSettled={handleBallSettled}
                  onAllNumbersSelected={handleAllNumbersSelected}
                  selectedNumbers={selectedNumbers}
                  isComplete={isComplete}
                  currentBallIndex={currentBallIndex}
                  onLaunchEnd={handleLaunchEnd}
                  isStarted={isStarted}
                />
              </div>
            </div>

            {/* Sidebar - hidden on mobile, visible on desktop */}
            <div className="hidden lg:block space-y-5">
              <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-5 border-2 border-amber-600/40 shadow-lg shadow-amber-900/20">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-gray-400 text-xs tracking-wider" style={{ fontFamily: "Orbitron, sans-serif" }}>PROGRESS</span>
                  <span className="text-amber-400 font-bold text-sm" style={{ fontFamily: "Orbitron, sans-serif" }}>
                    {progressCount} / 6
                  </span>
                </div>
                <div className="w-full bg-gray-700/50 rounded-full h-3 overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-amber-400 to-orange-500 transition-all duration-700 rounded-full relative"
                    style={{ width: `${(progressCount / 6) * 100}%` }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-pulse" />
                  </div>
                </div>
                <div className="flex justify-between mt-2">
                  {Array.from({ length: 6 }, (_, i) => (
                    <div
                      key={i}
                      className={`w-2 h-2 rounded-full transition-all duration-300 ${selectedNumbers[i] !== undefined ? "bg-amber-400 shadow-sm shadow-amber-400/50" : "bg-gray-700"}`}
                    />
                  ))}
                </div>
              </div>

              <ResultDisplay numbers={selectedNumbers} isComplete={isComplete} />
              <SharePanel numbers={selectedNumbers} isComplete={isComplete} />
              <ControlPanel
                onStart={handleStart}
                onReset={handleReset}
                onAutoGenerate={handleAutoGenerate}
                onCopy={handleCopy}
                isComplete={isComplete}
                isStarted={isStarted}
                isBallFlying={isBallFlying}
                hasNumbers={selectedNumbers.length > 0}
                currentBallIndex={currentBallIndex}
              />
            </div>
          </div>

          {/* Mobile: Share panel (only when complete) */}
          {isComplete && selectedNumbers.filter(n => n !== undefined).length === 6 && (
            <div className="lg:hidden px-3 py-4">
              <SharePanel numbers={selectedNumbers} isComplete={isComplete} />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="text-center pt-2 lg:pt-3 pb-safe px-4">
          <p className="text-white text-xs lg:text-base mb-1 lg:mb-2">
            ⚠️ 본 서비스는 재미용 랜덤 생성기이며 당첨을 보장하지 않습니다.
          </p>
          <p className="text-white text-xs lg:text-base mb-3">로또 구매는 신중히, 국밥은 과감히. 🍲</p>

          {/* 홈 화면에 추가 버튼 */}
          <button
            type="button"
            onClick={() => setShowInstallGuide(true)}
            className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-gray-800/70 border border-amber-600/30 text-amber-400/80 hover:text-amber-300 hover:border-amber-500/50 text-xs transition-all cursor-pointer whitespace-nowrap"
            style={{ fontFamily: "Orbitron, sans-serif" }}
          >
            <i className="ri-smartphone-line text-sm" />
            홈 화면에 추가
          </button>

          {/* Footer links & copyright */}
          <div className="mt-4 pt-3 border-t border-gray-700/40">
            <div className="flex items-center justify-center gap-3 mb-2">
              <Link to="/about" className="text-gray-500 hover:text-amber-400/80 text-[11px] transition-colors cursor-pointer whitespace-nowrap">About</Link>
              <span className="text-gray-700 text-[11px]">|</span>
              <Link to="/contact" className="text-gray-500 hover:text-amber-400/80 text-[11px] transition-colors cursor-pointer whitespace-nowrap">Contact</Link>
              <span className="text-gray-700 text-[11px]">|</span>
              <Link to="/privacy" className="text-gray-500 hover:text-amber-400/80 text-[11px] transition-colors cursor-pointer whitespace-nowrap">Privacy Policy</Link>
            </div>
            <p className="text-gray-600 text-[11px]">© 2026 나홀로 사장. All rights reserved.</p>
          </div>
        </div>
      </main>
    </div>
  );
}
